import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/atoms/button";
import QuestionBlock from "./questionBlock";
import QuestionCountBlock from "./questionCountBlock";

import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import {
  useStartQuestionMutation,
  useStartSectionMutation
} from "../../../../store/slices/api/assessmentApiSlice";

import { cn } from "../../../../utils/cn";
import GetHeroIcon from "../../../../utils/getHeroIcons";

import {
  Question,
  SectionProgressStatus
} from "../../../../types/assessmentOngoing";
import { useShowErrors } from "../toasterWrapper";

type Props = {
  ongoingSectionId: string;
  sectionProgress: SectionProgressStatus;
  isExpanded: boolean;
  onToggle: () => void;
};

const AssessmentSectionCard = ({
  ongoingSectionId,
  sectionProgress,
  isExpanded,
  onToggle
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [disableFunc, setDisableFunc] = useState(false);

  const showErrors = useShowErrors();

  const [startSection] = useStartSectionMutation();
  const [startQuestion] = useStartQuestionMutation();

  const ongoingId = useAppSelector((store) => store.AssessmentSlice.ongoingId);

  const ongoingSections = useAppSelector(
    (state) => state.AssessmentSlice.assessmentData?.ongoingSections
  );
  const targetSection =
    ongoingSections &&
    ongoingSections.find((el) => el.sectionId === ongoingSectionId);

  const sectionStatus = sectionProgress.status;

  const sectionProgressQuestionsValueList = Object.values(
    sectionProgress.questions
  );

  const totalCompletedQuestions = sectionProgressQuestionsValueList.filter(
    (question) => question.status === "completed"
  ).length;
  const totalFlaggedQuestions = sectionProgressQuestionsValueList.filter(
    (question) => question.flagged
  ).length;

  const getFirstUnansweredOrFlaggedQuestion = () => {
    const questions = sectionProgress.questions;
    const targetQuestion = Object.entries(questions)
      .sort((a, b) => a[1].orderInSection - b[1].orderInSection)
      .find(
        (el) => el[1].status === "not_started" || el[1].status === "unanswered"
      );
    console.log({ targetQuestion });

    return targetQuestion ? targetQuestion[0] : undefined;
  };
  const getFirstQuestionOfSection = () => {
    const questions = sectionProgress.questions;

    const questionIds = Object.keys(questions);
    return questionIds?.[0];
  };

  const startQuestionHandler = async (questionId: string) => {
    try {
      const questionProgress = sectionProgress.questions[questionId].status;

      if (!ongoingId) {
        throw {
          status: "",
          title: "Schedule Id Not Found",
          err: {},
          description: ""
        };
      }
      if (!questionProgress) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      if (questionProgress === "not_started") {
        const res = await startQuestion({
          actionType: "started",
          ongoingId: ongoingId,
          questionId: questionId
        });

        if (res.error) {
          throw {
            status: "",
            title: "Failed To Start The Question",
            err: res.error,
            description: ""
          };
        } else {
          dispatch(
            AssessmentSliceActions.mutateQuestionProgress({
              questionId: questionId,
              sectionId: targetSection?.sectionId,
              status: "unanswered"
            })
          );
          dispatch(
            AssessmentSliceActions.mutateActiveQuestionMeta({
              questionId: questionId,
              sectionId: targetSection?.sectionId
            })
          );
          dispatch(AssessmentSliceActions.mutateAssessmentView("question"));
        }
      } else {
        dispatch(
          AssessmentSliceActions.mutateActiveQuestionMeta({
            questionId: questionId,
            sectionId: targetSection?.sectionId as string
          })
        );
        dispatch(AssessmentSliceActions.mutateAssessmentView("question"));
      }
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
    }
  };

  const clickHandler = async (questionId: string) => {
    if (disableFunc) return;
    if (sectionProgress.status === "not_started") return;

    try {
      setDisableFunc(true);
      await startQuestionHandler(questionId);
      setDisableFunc(false);
    } catch (err) {
      console.error(err);
      setDisableFunc(false);
    }
  };

  const sectionStartButtonClickHandler = async () => {
    setDisableFunc(true);
    try {
      if (!ongoingId) {
        throw {
          status: "",
          title: "Ongoing Id Not Found",
          err: {},
          description: ""
        };
      }

      const res = await startSection({
        actionType: "started",
        ongoingId: ongoingId,
        sectionId: targetSection?.sectionId as string
      });

      if (res.error) {
        throw {
          status: "",
          title: "Failed To Start The Section",
          err: res.error,
          description: ""
        };
      } else {
        const questionId = getFirstUnansweredOrFlaggedQuestion();

        if (questionId) {
          await startQuestionHandler(questionId);
        }

        dispatch(
          AssessmentSliceActions.mutateSectionProgress({
            sectionId: targetSection?.sectionId as string,
            status: "in_progress"
          })
        );
        setDisableFunc(false);
      }
    } catch (err) {
      console.error(err);
      const temp: any = err;
      const rawErrList = temp?.err?.data?.error;
      const errList =
        rawErrList?.length > 0
          ? rawErrList?.map((el: string) => el.split("_")[0])
          : [];
      if (showErrors)
        showErrors(temp?.title, errList, "error", temp?.description);
      setDisableFunc(false);
    }
  };

  /* View Section */

  const viewSectionClickHandler = async () => {
    if (sectionProgress.status == "in_progress") return;
    setDisableFunc(true);
    const questionId = getFirstQuestionOfSection();

    if (questionId) {
      await startQuestionHandler(questionId);
      setDisableFunc(false);
    } else {
      setDisableFunc(false);
      return;
    }
  };

  /* continue  section */

  const sectionContinueButtonClickHandler = async () => {
    if (sectionProgress.status !== "in_progress") return;
    setDisableFunc(true);
    const questionId = getFirstUnansweredOrFlaggedQuestion();
    if (questionId) {
      await startQuestionHandler(questionId);
      setDisableFunc(false);
    } else {
      setDisableFunc(false);
      return;
    }
  };

  if (!targetSection) {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-8 space-y-2 text-red-700 border bg-red-50 border-border rounded-xl">
        <GetHeroIcon
          name="ExclamationTriangleIcon"
          variant="outline"
          className="w-5 h-5"
        />
        <span>{t("assessment.sectionCard.sectionNotFound")}</span>
      </div>
    );
  }

  return (
    <div
      data-tour="ad-sectionCard"
      className="px-6 py-3 border border-border rounded-xl bg-bkg"
    >
      {/* Main */}
      <div
        onClick={onToggle}
        className="flex flex-col items-start justify-between gap-5 cursor-pointer md:items-center md:flex-row lg:gap-10"
      >
        {/* Left */}
        <div
          className={cn(
            "flex items-center px-5 py-2 border-l-8 rounded border-primary flex-grow min-w-0",
            sectionStatus === "completed" && "border-[#00875D]",
            sectionStatus === "not_started" && "border-secondary",
            sectionStatus === "in_progress" && "border-amber-500"
          )}
        >
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium leading-6 text-secondary/85">
              {t("assessment.sectionCard.section")}{" "}
              {String(targetSection.orderInQp)}
            </p>
            <h2 className="text-xl leading-7 font-medium text-secondary break-all">
              {targetSection.title}
            </h2>
          </div>
        </div>
        {/* Right */}
        <div
          className={`flex flex-col sm:flex-row sm:items-center justify-between flex-shrink-0 md:w-auto w-full ${sectionStatus === "not_started" ? "" : "gap-8 md:gap-10"} md:w-fit sm:flex-row sm:items-center`}
        >
          {sectionStatus !== "not_started" && (
            <div className="flex items-center gap-5">
              <QuestionCountBlock
                variant="total"
                count={targetSection.totalQuestionCount}
              />
              <QuestionCountBlock
                variant="completed"
                count={totalCompletedQuestions}
              />
              <QuestionCountBlock
                variant="flagged"
                count={totalFlaggedQuestions}
              />
            </div>
          )}

          {sectionStatus === "not_started" && (
            <Button
              variant="outlined"
              buttonColor="secondary"
              disable={disableFunc}
              onClick={(e) => {
                sectionStartButtonClickHandler();
                e.stopPropagation();
              }}
            >
              {t("assessment.sectionCard.startSection")}
            </Button>
          )}
          <div className="flex flex-row md:flex-col lg:flex-col gap-4">
            {sectionStatus === "in_progress" && (
              <Button
                variant="outlined"
                buttonColor="secondary"
                onClick={(e) => {
                  sectionContinueButtonClickHandler();
                  e.stopPropagation();
                }}
              >
                {t("assessment.sectionCard.continueSection")}
              </Button>
            )}
            {sectionStatus === "completed" && (
              <>
                <div className="px-6 py-3 text-sm font-bold tracking-wider text-white rounded-full bg-primary">
                  {t("assessment.sectionCard.completed")}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {sectionStatus === "completed" && (
        <div className="flex justify-end pr-1.5 mt-2">
          <Button
            variant="outlined"
            buttonColor="secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              viewSectionClickHandler();
            }}
          >
            {t("assessment.sectionCard.viewSection")}
          </Button>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-5">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {Boolean(targetSection.totalQuestionCount) && (
              <span className="text-base font-semibold text-secondary/50">
                {t("assessment.sectionCard.totalQuestions")}{" "}
                {targetSection.totalQuestionCount}
              </span>
            )}
            {/* For future enhancement */}
            {/* {Boolean(targetSection.questionTypeCountMap.coding) && (
              <span className="text-base font-semibold text-secondary/50">
                Coding Questions: {targetSection.questionTypeCountMap.coding}
              </span>
            )}
            {Boolean(targetSection.questionTypeCountMap.objective) && (
              <span className="text-base font-semibold text-secondary/50">
                Objective Questions:{" "}
                {targetSection.questionTypeCountMap.objective}
              </span>
            )} */}
          </div>
          {/* Questions  */}
          <div className="flex flex-wrap items-center justify-start gap-5 mt-5">
            {targetSection.questions.map((question: Question) => {
              const questionBlockStatus =
                sectionProgress.questions[question.questionId].status;
              const questionFlagged =
                sectionProgress.questions[question.questionId].flagged;

              return (
                <QuestionBlock
                  key={question.id}
                  questionId={question.questionId}
                  number={question.orderInSection}
                  status={questionBlockStatus}
                  flagged={questionFlagged}
                  clickHandler={clickHandler}
                  disabled={disableFunc}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentSectionCard;
